import {
  CHANNEL_TYPES,
  COMPANIES,
  COMPANY_LABELS,
  CHANNEL_TYPE_LABELS,
  KPI_SOURCE_LABELS,
  PUBLISH_MODES,
  PUBLISH_MODE_LABELS,
  USER_ROLE_LABELS,
  formatNumber,
  type AreaDto,
  type ChannelDto,
  type ChannelType,
  type Company,
  type KpiDto,
  type PublishMode,
  type UserDto,
} from '@nova/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Boxes,
  Braces,
  Building2,
  Palette,
  Pencil,
  Radio,
  ShieldCheck,
  Target,
  Trash2,
  UserCheck,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { Button } from '../components/Button.js';
import { GlassPanel, PanelHeader } from '../components/GlassPanel.js';
import { SkeletonPanel } from '../components/Skeleton.js';
import { ApiDocsAdmin } from '../features/admin/ApiDocsAdmin.js';
import { AreasAdmin } from '../features/admin/AreasAdmin.js';
import { EmailAdmin } from '../features/admin/EmailAdmin.js';
import { ModulesAdmin } from '../features/admin/ModulesAdmin.js';
import { NewsletterAdmin } from '../features/admin/NewsletterAdmin.js';
import { BrandAdmin } from '../features/brand/BrandAdmin.js';
import { api } from '../lib/api.js';
import { cn } from '../lib/cn.js';

type Tab = 'areas' | 'brand' | 'users' | 'modules' | 'channels' | 'kpis' | 'api';

const TABS: { key: Tab; label: string; Icon: typeof Users; superadminOnly?: boolean }[] = [
  { key: 'areas', label: 'Områder', Icon: Building2 },
  { key: 'brand', label: 'Profil og tone', Icon: Palette },
  { key: 'users', label: 'Brukere', Icon: Users },
  { key: 'modules', label: 'Moduler', Icon: Boxes, superadminOnly: true },
  { key: 'channels', label: 'Kanaler', Icon: Radio },
  { key: 'kpis', label: 'KPI-er', Icon: Target },
  { key: 'api', label: 'API', Icon: Braces },
];

export function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('areas');
  const tabs = TABS.filter((t) => !t.superadminOnly || user?.isSuperadmin);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-ink text-xl">Administrasjon</h1>
        <p className="text-ink-soft mt-0.5 text-sm">
          Områder, profil og tone, brukere, purrelister, kanaler, KPI-definisjoner og
          API-dokumentasjon.
        </p>
      </header>

      <nav className="border-hairline flex gap-0.5 border-b">
        {tabs.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              'relative inline-flex items-center gap-1.5 px-3 py-2 text-xs transition',
              tab === key ? 'text-accent' : 'text-ink-soft hover:text-ink',
            )}
          >
            <Icon size={13} aria-hidden />
            {label}
            {tab === key && (
              <span
                aria-hidden
                className="bg-accent absolute inset-x-2 -bottom-px h-0.5 rounded-full"
              />
            )}
          </button>
        ))}
      </nav>

      {tab === 'areas' && <AreasAdmin />}
      {tab === 'brand' && <BrandAdmin />}
      {tab === 'users' && <UsersAdmin />}
      {tab === 'modules' && user?.isSuperadmin && <ModulesAdmin />}
      {tab === 'channels' && (
        <div className="space-y-3">
          <EmailAdmin />
          <NewsletterAdmin />
          <ChannelsAdmin />
        </div>
      )}
      {tab === 'kpis' && <KpisAdmin />}
      {tab === 'api' && <ApiDocsAdmin />}
    </div>
  );
}

// ── Brukere ─────────────────────────────────────────────────────────────────

function UsersAdmin() {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', 'admin'],
    queryFn: () => api.get<UserDto[]>('/users?includeInactive=true'),
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    title: '',
    password: '',
    role: 'MEMBER',
  });

  const createUser = useMutation({
    mutationFn: () => api.post('/users', form),
    onSuccess: () => {
      setCreating(false);
      setForm({ name: '', email: '', title: '', password: '', role: 'MEMBER' });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => api.patch(`/users/${id}`, { role }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  // Name, e-mail and title are edited inline on the row (07 — 2.1.9).
  const [editing, setEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', title: '' });

  // Which areas a person works in — assigned here, under the user (not per area).
  const [assigning, setAssigning] = useState<string | null>(null);
  const { data: areas = [] } = useQuery({
    queryKey: ['areas', 'assign'],
    queryFn: () => api.get<AreaDto[]>('/areas'),
  });

  const updateUser = useMutation({
    mutationFn: (id: string) =>
      api.patch(`/users/${id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim(),
        title: editForm.title.trim() || null,
      }),
    onSuccess: () => {
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const startEditing = (user: UserDto) => {
    updateUser.reset();
    setEditForm({ name: user.name, email: user.email, title: user.title ?? '' });
    setEditing(user.id);
  };

  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/users/${id}`, { isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  // Only a superadmin may grant superadmin, and only they see the control.
  const setSuperadmin = useMutation({
    mutationFn: ({ id, isSuperadmin }: { id: string; isSuperadmin: boolean }) =>
      api.patch(`/users/${id}`, { isSuperadmin }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  // Deletion is for cleaning up test accounts, so it asks first and only for
  // the one row (07-utviklingsplan.md 2.1.7).
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const removeUser = useMutation({
    mutationFn: (id: string) => api.delete(`/users/${id}`),
    onSuccess: () => {
      setConfirmDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });

  if (isLoading) return <SkeletonPanel lines={6} />;

  return (
    <GlassPanel padded={false}>
      <div className="px-5 pt-5">
        <PanelHeader
          title="Brukere"
          subtitle="Roller styrer hvem som kan godkjenne, publisere og administrere"
          icon={<Users size={16} />}
          action={
            <Button
              size="sm"
              icon={<UserPlus size={13} aria-hidden />}
              onClick={() => setCreating((v) => !v)}
            >
              Ny bruker
            </Button>
          }
        />
      </div>

      {creating && (
        <div className="border-hairline bg-base/30 mx-5 mb-4 grid gap-2 rounded-lg border p-3 sm:grid-cols-2">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Navn"
            className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
          />
          <input
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="E-post"
            type="email"
            className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
          />
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Tittel"
            className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
          />
          <input
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Midlertidig passord (minst 10 tegn)"
            type="text"
            className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
          >
            {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              loading={createUser.isPending}
              disabled={!form.name || !form.email || form.password.length < 10}
              onClick={() => createUser.mutate()}
            >
              Opprett
            </Button>
            <Button size="sm" onClick={() => setCreating(false)}>
              Avbryt
            </Button>
          </div>
          {createUser.isError && (
            <p className="text-danger text-xs sm:col-span-2">
              {(createUser.error as Error).message}
            </p>
          )}
        </div>
      )}

      <ul className="space-y-1 px-3 pb-3">
        {users.map((user) => (
          <li key={user.id} className="hover:bg-raised/50 rounded-lg px-2 py-2.5 transition">
            <div className="flex items-center gap-3">
              <span className="bg-accent/15 text-accent flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold">
                {user.name
                  .split(' ')
                  .slice(0, 2)
                  .map((p) => p[0])
                  .join('')}
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-ink block truncate text-sm">{user.name}</span>
                <span className="text-ink-soft block truncate text-[11px]">
                  {user.title ? `${user.title} · ` : ''}
                  {user.email}
                </span>
              </span>
              {!user.isActive && (
                <span className="border-ink-soft/25 text-ink-soft rounded-full border px-2 py-0.5 text-[11px]">
                  Deaktivert
                </span>
              )}
              <select
                value={user.role}
                aria-label={`Rolle for ${user.name}`}
                disabled={!user.isActive}
                onChange={(e) => updateRole.mutate({ id: user.id, role: e.target.value })}
                className="border-hairline bg-base/40 text-ink h-8 rounded-lg border px-2 text-[11px] disabled:opacity-40"
              >
                {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              {/* Superadmin: sees named human/system actors in the activity log.
                  Only a superadmin sees and controls this. */}
              {currentUser?.isSuperadmin && (
                <button
                  type="button"
                  aria-pressed={user.isSuperadmin}
                  aria-label={`Superadmin for ${user.name}`}
                  title={
                    user.isSuperadmin
                      ? 'Superadmin — ser navn i aktivitetsloggen. Klikk for å fjerne.'
                      : 'Gjør til superadmin (ser navn i aktivitetsloggen)'
                  }
                  disabled={setSuperadmin.isPending && setSuperadmin.variables?.id === user.id}
                  onClick={() =>
                    setSuperadmin.mutate({ id: user.id, isSuperadmin: !user.isSuperadmin })
                  }
                  className={cn(
                    'shrink-0 rounded p-1.5 transition',
                    user.isSuperadmin ? 'text-accent' : 'text-ink-soft hover:text-ink',
                  )}
                >
                  <ShieldCheck size={13} aria-hidden />
                </button>
              )}

              <button
                type="button"
                aria-label={`Områder for ${user.name}`}
                title="Områder brukeren jobber i"
                onClick={() => setAssigning(assigning === user.id ? null : user.id)}
                className={cn(
                  'shrink-0 rounded p-1.5 transition',
                  assigning === user.id ? 'text-accent' : 'text-ink-soft hover:text-ink',
                )}
              >
                <Building2 size={13} aria-hidden />
              </button>

              <button
                type="button"
                aria-label={`Rediger ${user.name}`}
                title="Rediger navn, tittel og e-post"
                onClick={() => (editing === user.id ? setEditing(null) : startEditing(user))}
                className={cn(
                  'shrink-0 rounded p-1.5 transition',
                  editing === user.id ? 'text-accent' : 'text-ink-soft hover:text-ink',
                )}
              >
                <Pencil size={13} aria-hidden />
              </button>

              {/* Deactivating is how a user is "removed": access goes away, but
                everything they own and everything they did stays intact. */}
              {user.id === currentUser?.id ? (
                <span className="text-ink-soft w-[104px] shrink-0 text-center text-[11px]">
                  Deg selv
                </span>
              ) : confirmDelete === user.id ? (
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="text-ink-soft text-[11px]">Slette permanent?</span>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={removeUser.isPending}
                    onClick={() => removeUser.mutate(user.id)}
                  >
                    Slett
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                    Avbryt
                  </Button>
                </span>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant={user.isActive ? 'ghost' : 'secondary'}
                    className="w-[104px] shrink-0"
                    icon={
                      user.isActive ? (
                        <UserMinus size={13} aria-hidden />
                      ) : (
                        <UserCheck size={13} aria-hidden />
                      )
                    }
                    loading={setActive.isPending && setActive.variables?.id === user.id}
                    onClick={() => setActive.mutate({ id: user.id, isActive: !user.isActive })}
                  >
                    {user.isActive ? 'Deaktiver' : 'Aktiver'}
                  </Button>
                  <button
                    type="button"
                    aria-label={`Slett ${user.name}`}
                    title="Slett permanent"
                    onClick={() => {
                      removeUser.reset();
                      setConfirmDelete(user.id);
                    }}
                    className="text-ink-soft hover:text-danger shrink-0 rounded p-1.5 transition"
                  >
                    <Trash2 size={13} aria-hidden />
                  </button>
                </>
              )}
            </div>

            {assigning === user.id && <AreaAssignment user={user} areas={areas} />}

            {editing === user.id && (
              <div className="border-hairline bg-base/30 mt-2 grid gap-2 rounded-lg border p-3 sm:grid-cols-2">
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Navn"
                  aria-label="Navn"
                  className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
                />
                <input
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  placeholder="E-post"
                  aria-label="E-post"
                  type="email"
                  className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
                />
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  placeholder="Tittel"
                  aria-label="Tittel"
                  className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    loading={updateUser.isPending}
                    disabled={editForm.name.trim().length < 2 || !editForm.email.includes('@')}
                    onClick={() => updateUser.mutate(user.id)}
                  >
                    Lagre
                  </Button>
                  <Button size="sm" onClick={() => setEditing(null)}>
                    Avbryt
                  </Button>
                </div>
                {updateUser.isError && (
                  <p className="text-danger text-xs sm:col-span-2">
                    {(updateUser.error as Error).message}
                  </p>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      <p className="text-ink-soft/70 px-5 pb-2 text-[11px] leading-relaxed">
        Deaktivering er det vanlige for folk som slutter: tilgangen forsvinner, men alt de eier og
        alt de har gjort står igjen. Sletting er for opprydding, og går bare når brukeren ikke eier
        noe.
      </p>

      {setActive.isError && (
        <p className="text-danger px-5 pb-4 text-xs">{(setActive.error as Error).message}</p>
      )}
      {removeUser.isError && (
        <p className="text-danger px-5 pb-4 text-xs">{(removeUser.error as Error).message}</p>
      )}
    </GlassPanel>
  );
}

/**
 * Which areas a person works in. A co-worker (MEMBER) gets access to the area
 * and is notified about comments on posts they are on — but is NOT chased by
 * reminders. Reminders follow the post's owner and the area's reminder list.
 */
function AreaAssignment({ user, areas }: { user: UserDto; areas: AreaDto[] }) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['areas'] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const add = useMutation({
    mutationFn: (areaId: string) =>
      api.post(`/areas/${areaId}/members`, { userId: user.id, role: 'MEMBER' }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (areaId: string) => api.delete(`/areas/${areaId}/members/${user.id}`),
    onSuccess: invalidate,
  });

  const busyId =
    (add.isPending && (add.variables as string)) ||
    (remove.isPending && (remove.variables as string)) ||
    null;

  return (
    <div className="border-hairline bg-base/30 mt-2 rounded-lg border p-3">
      <p className="label-micro mb-2">Jobber i disse områdene</p>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {areas.map((area) => {
          const membership = area.members?.find((m) => m.userId === user.id);
          const isOwner = area.ownerUserId === user.id || membership?.role === 'OWNER';
          const isReminder = membership?.role === 'REMINDER_LIST';
          const isMember = membership?.role === 'MEMBER';
          const locked = isOwner || isReminder;
          const busy = busyId === area.id;

          return (
            <button
              key={area.id}
              type="button"
              disabled={locked || busy}
              onClick={() => (isMember ? remove.mutate(area.id) : add.mutate(area.id))}
              className={cn(
                'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left text-xs transition disabled:cursor-default',
                isMember || isOwner || isReminder
                  ? 'border-accent/30 bg-accent/[0.08]'
                  : 'border-hairline bg-base/40 hover:border-accent/25',
              )}
            >
              <span
                aria-hidden
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: area.color }}
              />
              <span className="text-ink min-w-0 flex-1 truncate">{area.name}</span>
              {isOwner && <span className="text-ink-soft/70 shrink-0 text-[10px]">Ansvarlig</span>}
              {isReminder && (
                <span className="text-ink-soft/70 shrink-0 text-[10px]">På purreliste</span>
              )}
              {isMember && <span className="text-accent shrink-0 text-[10px]">Medarbeider</span>}
            </button>
          );
        })}
      </div>
      {(add.error || remove.error) && (
        <p className="text-danger mt-2 text-xs">{((add.error ?? remove.error) as Error).message}</p>
      )}
    </div>
  );
}

// ── Kanaler ─────────────────────────────────────────────────────────────────

const NEW_CHANNEL = {
  name: '',
  type: 'WEBSITE_AIMCMS' as ChannelType,
  company: 'BEGGE' as Company,
  publishMode: 'MANUAL_PREPARED' as PublishMode,
};

function ChannelsAdmin() {
  const queryClient = useQueryClient();
  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['channels', 'admin'],
    queryFn: () => api.get<ChannelDto[]>('/channels?includeInactive=true'),
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(NEW_CHANNEL);

  const createChannel = useMutation({
    mutationFn: () => api.post('/channels', form),
    onSuccess: () => {
      setCreating(false);
      setForm(NEW_CHANNEL);
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/channels/${id}`, { isActive }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['channels'] }),
  });

  if (isLoading) return <SkeletonPanel lines={6} />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-ink text-base">Kanaler</h2>
        <Button
          size="sm"
          icon={<Radio size={13} aria-hidden />}
          onClick={() => setCreating((v) => !v)}
        >
          Ny kanal
        </Button>
      </div>

      {creating && (
        <GlassPanel>
          <div className="grid gap-2 sm:grid-cols-2">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Navn (f.eks. «LinkedIn – Skjema.no»)"
              aria-label="Kanalnavn"
              className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm sm:col-span-2"
            />
            <label className="flex flex-col gap-1">
              <span className="label-micro">Type</span>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as ChannelType })}
                className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
              >
                {CHANNEL_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {CHANNEL_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-micro">Selskap</span>
              <select
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value as Company })}
                className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
              >
                {COMPANIES.map((c) => (
                  <option key={c} value={c}>
                    {COMPANY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 sm:col-span-2">
              <span className="label-micro">Publisering</span>
              <select
                value={form.publishMode}
                onChange={(e) => setForm({ ...form, publishMode: e.target.value as PublishMode })}
                className="border-hairline bg-base/50 text-ink h-9 rounded-lg border px-2.5 text-sm"
              >
                {PUBLISH_MODES.map((m) => (
                  <option key={m} value={m}>
                    {PUBLISH_MODE_LABELS[m]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              loading={createChannel.isPending}
              disabled={form.name.trim().length < 2}
              onClick={() => createChannel.mutate()}
            >
              Opprett kanal
            </Button>
            <Button size="sm" onClick={() => setCreating(false)}>
              Avbryt
            </Button>
            {createChannel.isError && (
              <span className="text-danger text-xs">{(createChannel.error as Error).message}</span>
            )}
          </div>
        </GlassPanel>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {channels.map((channel) => (
          <GlassPanel key={channel.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-display text-ink text-base">{channel.name}</h2>
                <p className="text-ink-soft mt-0.5 text-[11px]">
                  {CHANNEL_TYPE_LABELS[channel.type]} · {COMPANY_LABELS[channel.company]}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => toggle.mutate({ id: channel.id, isActive: !channel.isActive })}
              >
                {channel.isActive ? 'Slå av' : 'Slå på'}
              </Button>
            </div>

            <p className="border-hairline bg-base/30 text-ink-soft mt-3 rounded-lg border px-2.5 py-1.5 text-[11px]">
              {PUBLISH_MODE_LABELS[channel.publishMode]}
            </p>

            {Object.keys(channel.config).length > 0 && (
              <pre className="border-hairline bg-base/40 text-ink-soft mt-2 overflow-x-auto rounded-lg border p-2.5 font-mono text-[11px]">
                {JSON.stringify(channel.config, null, 2)}
              </pre>
            )}
          </GlassPanel>
        ))}
      </div>
    </div>
  );
}

// ── KPI-er ──────────────────────────────────────────────────────────────────

function KpisAdmin() {
  const { data: kpis = [], isLoading } = useQuery({
    queryKey: ['kpis', 'admin'],
    queryFn: () => api.get<KpiDto[]>('/kpis?includeInactive=true'),
  });

  if (isLoading) return <SkeletonPanel lines={6} />;

  return (
    <GlassPanel padded={false}>
      <div className="px-5 pt-5">
        <PanelHeader
          title="KPI-definisjoner"
          subtitle="Mål og kilder. Nova beregner de interne selv."
          icon={<Target size={16} />}
        />
      </div>
      <ul className="space-y-1 px-3 pb-3">
        {kpis.map((kpi) => (
          <li key={kpi.id} className="hover:bg-raised/50 rounded-lg px-2 py-2.5 transition">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-ink truncate text-sm">{kpi.name}</span>
              <span className="num-tabular text-ink-soft shrink-0 text-xs">
                Mål: {formatNumber(kpi.targetValue, 1)} {kpi.unit}
              </span>
            </div>
            <p className="text-ink-soft mt-0.5 text-[11px]">
              {KPI_SOURCE_LABELS[kpi.source]}
              {kpi.area ? ` · ${kpi.area.name}` : ' · Hele selskapet'}
            </p>
          </li>
        ))}
      </ul>
    </GlassPanel>
  );
}
